import PopUp, { closeModal, openModal } from "../PopUp.tsx";
import { IconDownload, IconShare2, IconSquarePlus } from "@tabler/icons-react";
import { useLocalStorage } from "../../../hooks/useLocalStorage.ts";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthToken } from "../../../hooks/useAuthToken.ts";

export function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function isInStandaloneMode() {
  return (
    ("standalone" in navigator && navigator.standalone === true) ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

export const MODAL_INSTALL_NOTICE = "install-notice";

const InstallNotice = () => {
  const { set, get } = useLocalStorage<boolean>("installNoticeShown", false);
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const { status } = useAuthToken();

  useEffect(() => {
    const handler = (e: Event) => {
      if (isInStandaloneMode()) return;
      if (get()) return;
      if (status !== "ok") return;

      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      openModal(MODAL_INSTALL_NOTICE);
    };
    window.addEventListener("beforeinstallprompt", handler);

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [get, status]);
  const install = async () => {
    if (!installPrompt) return false;
    try {
      await installPrompt.prompt();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  if (isInStandaloneMode()) {
    return (
      <PopUp
        id={MODAL_INSTALL_NOTICE}
        position="modal-middle"
        title={
          <>
            <IconDownload />
            Schon installiert.
          </>
        }
      >
        <button
          className="btn btn-neutral w-full"
          onClick={async () => {
            set(true);
            closeModal(MODAL_INSTALL_NOTICE);
          }}
        >
          Fertig
        </button>
      </PopUp>
    );
  }

  if (isIos() && !isInStandaloneMode()) {
    return (
      <PopUp
        id={MODAL_INSTALL_NOTICE}
        position="modal-middle"
        title={
          <>
            <IconDownload />
            Füge Surfanzeigen als App hinzu.
          </>
        }
      >
        <div className="card-body items-center">
          <h6 className="card-title">Installation unter IOS</h6>
          <ul className="list shadow-lg">
            <li className="p-4 pb-2 text-xs opacity-60 tracking-wide">
              Apple unterstützt die Installation auf den Homescreen. Folgende
              Schritte sind Nötig. Falls du einen anderen Browser als Safari
              nutzt, wechsel zu Safari:
            </li>

            <li className="list-row">
              <div>
                <IconShare2 />
              </div>
              <div className="list-col-grow">
                <div>Teilen</div>
                <div className="text-xs uppercase font-semibold opacity-60">
                  Gehe in Safari auf "teilen".
                </div>
              </div>
            </li>

            <li className="list-row">
              <div>
                <IconSquarePlus />
              </div>
              <div className="list-col-grow">
                <div>Zum Home-Bildschirm</div>
                <div className="text-xs uppercase font-semibold opacity-60">
                  Falls du den Knopf "Zum Home-Bildschirm" nicht siehst, gehe
                  auf "mehr".
                </div>
              </div>
            </li>
          </ul>
          <Link
            className="link"
            to="https://support.apple.com/de-de/guide/iphone/iphea86e5236/ios"
          >
            Mehr Informationen auf der Apple-Support-Seite
          </Link>
          <div className="card-actions justify-between">
            <button
              className="btn btn-neutral"
              onClick={async () => {
                set(true);
                closeModal(MODAL_INSTALL_NOTICE);
              }}
            >
              Fertig
            </button>
          </div>
        </div>
      </PopUp>
    );
  }
  if (!installPrompt)
    return (
      <PopUp
        id={MODAL_INSTALL_NOTICE}
        position="modal-middle"
        title={
          <>
            <IconDownload />
            Füge Surfanzeigen als App hinzu.
          </>
        }
      >
        <div className="card-body items-center">
          <h6 className="card-title">Hoppla...</h6>
          <p>
            Es scheint, dass dein Gerät oder Browser dieses Feature nicht
            unterstützt. Vielleicht hab ich auch einen Fehler gemacht. Schaue
            doch mal nach dem Suchbegriff "Webseite als Web-App auf [dein Gerät
            oder Browser] installieren".
          </p>
          <div className="card-actions justify-between">
            <button
              className="btn btn-neutral"
              onClick={async () => {
                set(true);
                closeModal(MODAL_INSTALL_NOTICE);
              }}
            >
              Weiter im Browser
            </button>
          </div>
        </div>
      </PopUp>
    );
  return (
    <PopUp
      id={MODAL_INSTALL_NOTICE}
      position="modal-middle"
      title={
        <>
          <IconDownload />
          Füge Surfanzeigen als App hinzu.
        </>
      }
    >
      <div className="card-body items-center">
        <h6 className="card-title">Immer Griffbereit.</h6>
        <p>Viel Spaß beim Surfen!</p>
        <div className="card-actions justify-between">
          <button
            className="btn btn-neutral"
            onClick={() => {
              set(true);
              closeModal(MODAL_INSTALL_NOTICE);
            }}
          >
            Weiter im Browser
          </button>
          <button
            className="btn btn-success"
            onClick={async () => {
              set(true);
              await install();
              closeModal(MODAL_INSTALL_NOTICE);
            }}
          >
            Installieren
          </button>
        </div>
      </div>
    </PopUp>
  );
};
export default InstallNotice;
